import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  Clock, CheckCircle, AlertCircle, XCircle, Search,
  FileText, Shield, GraduationCap, ArrowRight, RefreshCw
} from 'lucide-react';
import Navigation from '../components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverApplicationStatus = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (location.state?.email) {
      checkStatus(location.state.email);
    }
  }, [location.state]);

  const checkStatus = async (emailToCheck) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/observer/application-status`,
        { params: { email: emailToCheck || email } }
      );
      
      if (response.data.found) {
        setStatus(response.data);
      } else {
        setError('No application found with this email address.');
        setStatus(null);
      }
    } catch (err) {
      setError('Failed to check status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (statusCode) => {
    switch (statusCode) {
      case 'pending_nda':
        return {
          icon: FileText,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-100',
          borderColor: 'border-yellow-500',
          label: 'Pending NDA',
          step: 1
        };
      case 'pending_verification':
        return {
          icon: Shield,
          color: 'text-blue-600',
          bgColor: 'bg-blue-100',
          borderColor: 'border-blue-500',
          label: 'Under Review',
          step: 2
        };
      case 'pending_training':
        return {
          icon: GraduationCap,
          color: 'text-purple-600',
          bgColor: 'bg-purple-100',
          borderColor: 'border-purple-500',
          label: 'Training Required',
          step: 3
        };
      case 'approved':
        return {
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          borderColor: 'border-green-500',
          label: 'Approved',
          step: 4
        };
      case 'rejected':
        return {
          icon: XCircle,
          color: 'text-red-600',
          bgColor: 'bg-red-100',
          borderColor: 'border-red-500',
          label: 'Not Approved',
          step: 0
        };
      default:
        return {
          icon: Clock,
          color: 'text-gray-600',
          bgColor: 'bg-gray-100',
          borderColor: 'border-gray-500',
          label: 'Processing',
          step: 1
        };
    }
  };

  const steps = [
    { id: 1, label: 'Application', desc: 'Submit your details' },
    { id: 2, label: 'NDA Signing', desc: 'Sign agreements' },
    { id: 3, label: 'Verification', desc: 'Background check' },
    { id: 4, label: 'Training', desc: 'Complete training' },
    { id: 5, label: 'Active', desc: 'Start observing' }
  ];

  const statusConfig = status ? getStatusConfig(status.status) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50">
      <Navigation />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Application Status</h1>
            <p className="text-gray-600 mt-2">Track your Observer application progress</p>
          </div>

          {/* Search Form */}
          {!status && (
            <Card className="shadow-xl border-0 mb-8">
              <CardContent className="p-6">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className="w-full"
                    />
                  </div>
                  <Button
                    onClick={() => checkStatus()}
                    disabled={loading || !email}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {loading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Check Status
                      </>
                    )}
                  </Button>
                </div>
                
                {error && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                    {error}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Status Display */}
          {status && statusConfig && (
            <>
              {/* Current Status Card */}
              <Card className={`shadow-xl border-0 border-l-4 ${statusConfig.borderColor} mb-8`}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 rounded-full ${statusConfig.bgColor} flex items-center justify-center`}>
                      <statusConfig.icon className={`w-8 h-8 ${statusConfig.color}`} />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-gray-900">{statusConfig.label}</h2>
                      <p className="text-gray-600">{status.status_message}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        Applied on: {new Date(status.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      onClick={() => checkStatus(email)}
                      disabled={loading}
                    >
                      <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Progress Steps */}
              <Card className="shadow-xl border-0 mb-8">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-6">Application Progress</h3>
                  
                  <div className="relative">
                    {/* Progress Line */}
                    <div className="absolute left-6 top-6 bottom-6 w-0.5 bg-gray-200" />
                    
                    {steps.map((step, idx) => {
                      const isCompleted = statusConfig.step >= step.id;
                      const isCurrent = statusConfig.step === step.id;
                      
                      return (
                        <div key={step.id} className="flex items-center gap-4 mb-6 last:mb-0 relative">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center z-10 ${
                            isCompleted ? 'bg-green-500 text-white' :
                            isCurrent ? 'bg-purple-500 text-white' :
                            'bg-gray-200 text-gray-500'
                          }`}>
                            {isCompleted && !isCurrent ? (
                              <CheckCircle className="w-6 h-6" />
                            ) : (
                              <span className="font-semibold">{step.id}</span>
                            )}
                          </div>
                          <div className={`flex-1 ${isCurrent ? 'bg-purple-50 -mx-2 px-4 py-2 rounded-lg' : ''}`}>
                            <p className={`font-medium ${isCurrent ? 'text-purple-900' : 'text-gray-900'}`}>
                              {step.label}
                            </p>
                            <p className="text-sm text-gray-500">{step.desc}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex flex-col gap-4">
                {status.status === 'pending_nda' && (
                  <Button
                    onClick={() => navigate('/observer/nda')}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    Complete NDA Signing
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
                
                {status.status === 'pending_training' && (
                  <Button
                    onClick={() => navigate('/observer/training')}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    Start Training
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
                
                {status.status === 'approved' && (
                  <Button
                    onClick={() => navigate('/observer/login')}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    Login to Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}

                <Button
                  variant="outline"
                  onClick={() => {
                    setStatus(null);
                    setEmail('');
                  }}
                >
                  Check Another Application
                </Button>
              </div>
            </>
          )}

          {/* Help Section */}
          <Card className="shadow-md border-0 mt-8 bg-blue-50">
            <CardContent className="p-6">
              <h3 className="font-semibold text-blue-900 mb-2">Need Help?</h3>
              <p className="text-blue-800 text-sm">
                If you have questions about your application or need assistance, please contact us at{' '}
                <a href="mailto:support@sanjaya.com" className="underline font-medium">
                  support@sanjaya.com
                </a>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ObserverApplicationStatus;
